/* ==========================================================================
   Landing page behaviour (lp-* sections)

   Every widget is a custom element, so it works in the theme editor:
   Shopify re-renders a section on change and connectedCallback runs again.

     <lp-countdown>    the sale timer
     <lp-gallery>      image carousel + thumbnails + rotating testimonial
     <lp-bundles>      variant selector, price, gift unlock bar, delivery date
     <lp-accordion>    buy box and FAQ accordions
     <lp-sticky>       sticky add-to-cart bar
   ========================================================================== */

/* -------------------------------------------------------------------------
   COUNTDOWN
   Attributes: minutes (number) — restarts on every page load.
   ------------------------------------------------------------------------- */
class LpCountdown extends HTMLElement {
  connectedCallback() {
    const minutes = parseInt(this.dataset.minutes || '120', 10);
    this.end = Date.now() + minutes * 60 * 1000;
    this.h = this.querySelector('[data-h]');
    this.m = this.querySelector('[data-m]');
    this.s = this.querySelector('[data-s]');
    if (!this.h) return;
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  tick() {
    const left = Math.max(0, Math.floor((this.end - Date.now()) / 1000));
    const pad = (n) => String(n).padStart(2, '0');
    this.h.textContent = pad(Math.floor(left / 3600));
    this.m.textContent = pad(Math.floor((left % 3600) / 60));
    this.s.textContent = pad(left % 60);
  }
}
customElements.define('lp-countdown', LpCountdown);

/* -------------------------------------------------------------------------
   GALLERY
   ------------------------------------------------------------------------- */
class LpGallery extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.lp-gallery__slide'));
    this.thumbs = Array.from(this.querySelectorAll('.lp-gallery__thumbs button'));
    this.index = 0;

    const prev = this.querySelector('.lp-gallery__arrow--prev');
    const next = this.querySelector('.lp-gallery__arrow--next');
    if (prev) prev.addEventListener('click', () => this.go(this.index - 1));
    if (next) next.addEventListener('click', () => this.go(this.index + 1));
    this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.go(i)));

    this.initQuotes();
  }

  disconnectedCallback() {
    clearInterval(this.quoteTimer);
  }

  go(n) {
    if (!this.slides.length) return;
    this.index = (n + this.slides.length) % this.slides.length;
    this.slides.forEach((s, i) => s.classList.toggle('is-active', i === this.index));
    this.thumbs.forEach((t, i) => t.classList.toggle('is-active', i === this.index));
  }

  /* Rotates through the testimonial blocks placed in this section. */
  initQuotes() {
    this.quotes = Array.from(this.querySelectorAll('[data-quote]'));
    if (this.quotes.length < 2) return;
    let i = 0;
    this.quoteTimer = setInterval(() => {
      this.quotes[i].hidden = true;
      i = (i + 1) % this.quotes.length;
      this.quotes[i].hidden = false;
    }, parseInt(this.dataset.rotate || '6000', 10));
  }
}
customElements.define('lp-gallery', LpGallery);

/* -------------------------------------------------------------------------
   BUNDLES
   Each .lp-bundle carries:
     data-variant  Shopify variant id
     data-now      selling price, formatted
     data-was      compare-at price, formatted
     data-save     amount saved, formatted
     data-tier     1..n, drives the gift unlock bar
   ------------------------------------------------------------------------- */
class LpBundles extends HTMLElement {
  connectedCallback() {
    /* The buy column is a block stream now, so the shipping bar and the
       delivery bar can both be on a page that has no bundle options at all.
       Their date is rendered before the guard below for that reason. */
    this.renderDeliveryDate();

    this.cards = Array.from(this.querySelectorAll('.lp-bundle'));
    if (!this.cards.length) return;

    this.variantInput = this.querySelector('[name="id"]');
    this.qtyInput = this.querySelector('[data-lp-qty]');
    this.planInput = this.querySelector('[data-lp-plan]');
    this.form = this.querySelector('form[data-type="add-to-cart-form"]');
    this.priceNow = this.querySelector('[data-price-now]');
    this.priceWas = this.querySelector('[data-price-was]');
    this.priceTag = this.querySelector('[data-price-tag]');
    this.atcPrice = this.querySelector('[data-atc-price]');
    this.fill = this.querySelector('.lp-unlock__fill');
    this.head = this.querySelector('[data-unlock-head]');
    this.steps = Array.from(this.querySelectorAll('.lp-unlock__step'));

    this.cards.forEach((card) => {
      card.addEventListener('click', () => this.select(card));
    });

    this.bindAtcAction();
    this.bindGiftAdd();
    this.bindSubscribe();
    if (this.form) {
      this.form.addEventListener('submit', () => this.disableForeignPlanFields(), true);
    }

    const preselected = this.cards.find((c) => c.dataset.default === 'true');
    this.select(preselected || this.cards[0]);
  }

  /* SUBSCRIPTIONS
     Nothing about the plan is configured in the theme editor. Each option
     carries the plan id, the interval and the discount that Shopify itself
     reports for that variant, so the box can never advertise a frequency or a
     percentage the cart will not honour. An option with no plan on it hides
     the box entirely rather than offering a subscription that cannot exist. */
  bindSubscribe() {
    this.wantSub = false;
    this.subBox = this.querySelector('[data-lp-subscribe]');
    if (!this.subBox) return;

    this.wantSub = this.subBox.dataset.default !== 'false';
    this.subToggle = this.subBox.querySelector('[data-sub-toggle]');
    if (!this.subToggle) return;

    this.subToggle.addEventListener('click', () => {
      this.wantSub = !this.wantSub;
      const active = this.cards.find((c) => c.classList.contains('is-active'));
      if (active) this.select(active);
    });
  }

  /* The subscription app renders its own picker inside the product form and its
     radios are called selling_plan too. CSS hides that picker, but a hidden
     radio is still posted, so a second and contradictory plan would reach the
     cart. Everything called selling_plan that is not our own field is therefore
     disabled, which takes it out of the FormData. Run again as the form is
     submitted, in the capture phase, because the app can render after we do. */
  disableForeignPlanFields() {
    if (!this.form) return;
    this.form.querySelectorAll('[name="selling_plan"]').forEach((field) => {
      if (field !== this.planInput) field.disabled = true;
    });
  }

  /* Every option repaints, not just the chosen one, so the three prices stay
     comparable with each other instead of one subscription price sitting in a
     column of one-time prices. */
  paintRow(card, subOn) {
    const on = subOn && !!card.dataset.plan;
    const d = card.dataset;
    const values = {
      '[data-row-now]': (on && d.subNow) || d.now,
      '[data-row-was]': on ? d.subWas : d.was,
      '[data-row-save]': on ? d.subSave : d.save,
      '[data-row-each]': on ? d.subEach : d.each,
    };

    Object.keys(values).forEach((selector) => {
      const el = card.querySelector(selector);
      if (!el) return;
      const value = values[selector] || '';
      const template = el.dataset.tpl;
      /* A template means the element wraps the amount in wording the merchant
         typed, so the amount is substituted rather than printed bare. */
      el.textContent = template ? template.replace('[amount]', value) : value;
      if (selector !== '[data-row-now]') el.hidden = !value || template === '';
    });
  }

  paintSubscribeBox(card, subOn) {
    if (!this.subBox) return;

    const plan = card.dataset.plan;
    this.subBox.hidden = !plan;
    if (!plan) return;

    const count = parseInt(card.dataset.planFreq || '1', 10);
    const unit = card.dataset.planUnit || 'month';
    const every = ('every ' + (count > 1 ? count + ' ' + unit + 's' : unit)).replace(
      /\b[a-z]/g,
      (letter) => letter.toUpperCase()
    );

    const heading = this.subBox.querySelector('[data-sub-heading]');
    if (heading) {
      heading.textContent = (heading.dataset.tpl || '').replace('[percent]', card.dataset.planPct || '');
    }
    const second = this.subBox.querySelector('[data-sub-sub]');
    if (second) second.textContent = (second.dataset.tpl || '').replace('[every]', every);

    this.subBox.classList.toggle('is-on', subOn);
    if (this.subToggle) this.subToggle.setAttribute('aria-checked', String(subOn));
  }

  /* Where the customer lands once the item is in the cart. "drawer" needs no
     code here: Dawn's own cart drawer opens itself. The other two wait for
     Dawn to confirm the add, then leave. */
  bindAtcAction() {
    /* The choice belongs to the Add to cart block, which is one row in a
       reorderable stream rather than this element, so it is read from
       wherever in the subtree that block ended up. */
    const holder = this.querySelector('[data-atc-action]');
    const action = holder && holder.dataset.atcAction;
    if (action !== 'checkout' && action !== 'cart') return;
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    /* Built off cart_url so a market or locale prefix survives the redirect. */
    const cartUrl = (window.routes || {}).cart_url || '/cart';
    const destination = action === 'checkout' ? cartUrl + '/checkout' : cartUrl;

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'product-form') window.location = destination;
    });
  }

  /* Shopify's own add-to-cart form posts exactly one line, so an order that is
     meant to arrive with gifts in it has to be posted as a multi-line add
     instead. This only takes over the submit when there is actually a gift to
     add; otherwise Dawn handles it as before. The gift variants must be priced
     at 0.00 in admin — a line item costs whatever its variant costs, and no
     amount of front-end code changes that. */
  bindGiftAdd() {
    this.giftSteps = Array.from(this.querySelectorAll('.lp-unlock__step[data-free-variant]')).filter(
      (s) => s.dataset.freeVariant
    );
    if (!this.giftSteps.length || !this.form) return;

    this.form.addEventListener('submit', (event) => this.onSubmitWithGifts(event), true);
  }

  giftsForTier(tier) {
    return this.giftSteps
      .filter((s) => tier >= parseInt(s.dataset.at || '1', 10))
      .map((s) => ({
        id: Number(s.dataset.freeVariant),
        quantity: 1,
        properties: { _free_gift: s.dataset.giftTitle || 'Gift' },
      }))
      .filter((item) => item.id);
  }

  onSubmitWithGifts(event) {
    const card = this.cards.find((c) => c.classList.contains('is-active'));
    if (!card) return;

    const gifts = this.giftsForTier(parseInt(card.dataset.tier || '1', 10));
    if (!gifts.length) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const main = { id: Number(card.dataset.variant), quantity: parseInt(card.dataset.qty || '1', 10) };
    /* This path builds its own payload, so the plan has to be carried across
       by hand — the hidden field only reaches the cart on the normal submit. */
    if (this.planInput && !this.planInput.disabled && this.planInput.value) {
      main.selling_plan = Number(this.planInput.value);
    }

    /* Same busy state Dawn's own handler puts up, so the button behaves
       identically whether or not the option carries a gift. */
    const button = this.form.querySelector('[name="add"]');
    const spinner = this.form.querySelector('.loading__spinner');
    const busy = (on) => {
      if (button) {
        button.setAttribute('aria-disabled', String(on));
        button.classList.toggle('loading', on);
      }
      if (spinner) spinner.classList.toggle('hidden', !on);
    };
    if (button && button.getAttribute('aria-disabled') === 'true') return;
    busy(true);

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const body = {
      items: [main].concat(gifts),
      sections: cart ? cart.getSectionsToRender().map((s) => s.id) : [],
      sections_url: window.location.pathname,
    };

    const post = (payload) =>
      fetch((window.routes || {}).cart_add_url || '/cart/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify(payload),
      }).then((r) => r.json());

    post(body)
      /* A sold-out or mistyped gift variant rejects the whole add. The order
         still matters more than the gift, so the main line goes in alone. */
      .then((res) => (res.status ? post(Object.assign({}, body, { items: [main] })) : res))
      .then((res) => {
        if (cart && !res.status) cart.renderContents(res);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form' });
        }
      })
      .finally(() => busy(false));
  }

  select(card) {
    this.cards.forEach((c) => c.classList.remove('is-active'));
    card.classList.add('is-active');

    const radio = card.querySelector('input[type="radio"]');
    if (radio) radio.checked = true;

    const subOn = !!this.wantSub && !!card.dataset.plan;
    this.paintSubscribeBox(card, subOn);
    this.cards.forEach((c) => this.paintRow(c, subOn));

    if (this.variantInput) this.variantInput.value = card.dataset.variant || '';
    if (this.qtyInput) this.qtyInput.value = card.dataset.qty || '1';
    /* Disabled fields are left out of the FormData Dawn posts, so switching
       the subscription off removes selling_plan rather than sending it empty,
       which /cart/add rejects. */
    if (this.planInput) {
      this.planInput.value = subOn ? card.dataset.plan : '';
      this.planInput.disabled = !subOn;
    }
    this.disableForeignPlanFields();

    const now = (subOn && card.dataset.subNow) || card.dataset.now;
    const was = subOn ? card.dataset.subWas : card.dataset.was;
    const save = subOn ? card.dataset.subSave : card.dataset.save;
    if (this.priceNow && now) this.priceNow.textContent = now;
    if (this.priceWas) {
      this.priceWas.textContent = was || '';
      this.priceWas.hidden = !was;
    }
    if (this.priceTag) {
      this.priceTag.textContent = this.priceTag.dataset.priceTag.replace('[amount]', save || '');
      this.priceTag.hidden = !save;
    }
    if (this.atcPrice && now) this.atcPrice.textContent = now;

    this.updateUnlock(parseInt(card.dataset.tier || '1', 10));

    /* Let the sticky bar mirror the choice. */
    document.dispatchEvent(
      new CustomEvent('lp:bundle-change', {
        detail: {
          variant: card.dataset.variant,
          price: now,
          name: (card.querySelector('.lp-bundle__name') || {}).textContent,
        },
      })
    );
  }

  updateUnlock(tier) {
    if (!this.steps.length) return;
    this.steps.forEach((step) => {
      const done = tier >= parseInt(step.dataset.at || '1', 10);
      step.classList.toggle('is-done', done);
      const ico = step.querySelector('.lp-unlock__ico');
      // An icon showing the gift's own picture keeps it; only the emoji swaps.
      if (ico && !ico.hasAttribute('data-has-image')) ico.textContent = done ? '✓' : '🔒';
    });
    const done = this.steps.filter((s) => s.classList.contains('is-done')).length;
    if (this.fill) this.fill.style.width = (done / this.steps.length) * 100 + '%';
    if (this.head) {
      this.head.textContent =
        done >= this.steps.length
          ? this.head.dataset.allText
          : this.head.dataset.someText
              .replace('[count]', done)
              .replace('[total]', this.steps.length);
    }
  }

  /* today + N business days, in the shopper's own locale */
  /* Working days from today, rendered in the shopper's own locale.
     data-days sets the lead time, data-format picks long or short. */
  renderDeliveryDate() {
    const targets = this.querySelectorAll('[data-delivery-date]');
    if (!targets.length) return;

    targets.forEach((target) => {
      const days = parseInt(target.dataset.days || '3', 10);
      const d = new Date();
      let added = 0;
      while (added < days) {
        d.setDate(d.getDate() + 1);
        if (d.getDay() !== 0 && d.getDay() !== 6) added += 1;
      }

      const short = target.dataset.format === 'short';
      target.textContent = d.toLocaleDateString(document.documentElement.lang || undefined, {
        weekday: short ? 'short' : 'long',
        month: short ? 'short' : 'long',
        day: 'numeric',
      });
    });
  }
}
customElements.define('lp-bundles', LpBundles);

/* -------------------------------------------------------------------------
   VIDEO CARD

   Loads muted with no controls so it reads as a poster. The first click hands
   it over to the browser's own player: sound on, controls on, overlay gone.
   Playing one card stops the others, so two clips never talk over each other.
   ------------------------------------------------------------------------- */
class LpVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.button = this.querySelector('.lp-video__play');
    if (!this.video || !this.button) return;

    this.button.addEventListener('click', () => this.play());
    this.video.addEventListener('pause', () => this.classList.remove('is-playing'));
  }

  play() {
    document.querySelectorAll('lp-video.is-playing').forEach((other) => {
      if (other !== this) other.stop();
    });

    this.classList.add('is-playing');
    this.video.muted = false;
    this.video.controls = true;
    const started = this.video.play();
    if (started) started.catch(() => this.classList.remove('is-playing'));
  }

  stop() {
    this.classList.remove('is-playing');
    this.video.pause();
  }
}
customElements.define('lp-video', LpVideo);

/* -------------------------------------------------------------------------
   ACCORDION
   ------------------------------------------------------------------------- */
class LpAccordion extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('.lp-acc__btn').forEach((btn) => {
      btn.addEventListener('click', () => this.toggle(btn));
    });
  }

  toggle(btn) {
    const item = btn.closest('.lp-acc__item');
    const body = item.querySelector('.lp-acc__body');
    const open = !item.classList.contains('is-open');

    if (this.dataset.single === 'true' && open) {
      this.querySelectorAll('.lp-acc__item.is-open').forEach((other) => {
        other.classList.remove('is-open');
        other.querySelector('.lp-acc__body').style.maxHeight = 0;
        other.querySelector('.lp-acc__btn').setAttribute('aria-expanded', 'false');
      });
    }

    item.classList.toggle('is-open', open);
    btn.setAttribute('aria-expanded', String(open));
    body.style.maxHeight = open ? body.scrollHeight + 'px' : 0;
  }
}
customElements.define('lp-accordion', LpAccordion);

/* -------------------------------------------------------------------------
   STICKY ADD TO CART
   Appears once the main add-to-cart button has scrolled out of view.
   ------------------------------------------------------------------------- */
class LpSticky extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('.lp-sticky');
    this.label = this.querySelector('[data-sticky-sub]');
    this.button = this.querySelector('[data-sticky-button]');

    /* The bar walks the customer back to the options rather than adding to the
       cart behind their back, so the option they end up buying is one they
       actually chose. The bundles are the target when they exist and the main
       button is the fallback, which is also the thing whose position decides
       whether this bar is on screen at all. */
    if (this.button) {
      this.button.addEventListener('click', () => {
        const target = document.querySelector('.lp-bundles') || document.querySelector('[data-lp-atc]');
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }

    document.addEventListener('lp:bundle-change', (event) => {
      if (!this.label) return;
      const { name, price } = event.detail;
      this.label.textContent = [name, price].filter(Boolean).join(' — ');
    });

    this.watch();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  watch() {
    const anchor = document.querySelector('[data-lp-atc]');
    if (!anchor || !this.bar) return;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.bar.classList.toggle('is-visible', scrolledPast);
      },
      { threshold: 0 }
    );
    this.observer.observe(anchor);
  }
}
customElements.define('lp-sticky', LpSticky);

/* -------------------------------------------------------------------------
   MODAL
   Used by the supplement-facts button. Body scroll is locked while open so
   the page behind does not move under the panel on touch.
   ------------------------------------------------------------------------- */
class LpModal extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-modal-open]');
    this.wrap = this.querySelector('.lp-modal__wrap');
    this.panel = this.querySelector('.lp-modal__panel');
    if (!this.trigger || !this.wrap) return;

    this.trigger.addEventListener('click', () => this.open());
    this.querySelectorAll('[data-modal-close]').forEach((el) => {
      el.addEventListener('click', () => this.close());
    });
    this.onKey = (event) => {
      if (event.key === 'Escape') this.close();
    };
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKey);
  }

  open() {
    this.wrap.hidden = false;
    document.body.classList.add('overflow-hidden');
    document.addEventListener('keydown', this.onKey);
    this.trigger.setAttribute('aria-expanded', 'true');
    if (this.panel) this.panel.focus();
  }

  close() {
    this.wrap.hidden = true;
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keydown', this.onKey);
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.focus();
  }
}
customElements.define('lp-modal', LpModal);

/* -------------------------------------------------------------------------
   TIMELINE
   Paints the rail solid down to wherever the reader has got to, and ticks
   each dot as it is passed. The reference point is the middle of the
   viewport, so a step reads as "reached" when it is actually being looked at
   rather than when it first peeks in at the bottom.
   ------------------------------------------------------------------------- */
class LpTimeline extends HTMLElement {
  connectedCallback() {
    this.rail = this.querySelector('.lp-timeline__rail');
    this.rows = [...this.querySelectorAll('.lp-timeline__row')];
    if (!this.rail || !this.rows.length) return;

    this.onScroll = () => {
      if (this.queued) return;
      this.queued = true;
      requestAnimationFrame(() => {
        this.queued = false;
        this.update();
      });
    };

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll);
    this.update();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
  }

  update() {
    const rail = this.rail.getBoundingClientRect();
    /* Wide screens lay the rail across the top instead of down the side. A
       horizontal rail is only a few pixels tall, so its own height cannot
       drive the progress — the whole timeline's height does. */
    const across = rail.width > rail.height;
    const track = across ? this.getBoundingClientRect() : rail;
    if (!track.height) return;

    const mark = window.innerHeight / 2;
    const ratio = Math.min(Math.max((mark - track.top) / track.height, 0), 1);
    this.style.setProperty('--tl-progress', (ratio * 100).toFixed(2) + '%');

    const reached = across ? rail.left + rail.width * ratio : rail.top + rail.height * ratio;
    this.rows.forEach((row) => {
      const dot = row.querySelector('.lp-timeline__dot');
      if (!dot) return;
      const box = dot.getBoundingClientRect();
      const centre = across ? box.left + box.width / 2 : box.top + box.height / 2;
      row.classList.toggle('is-passed', centre <= reached);
    });
  }
}
customElements.define('lp-timeline', LpTimeline);
